import { Avatar, Drawer, Dropdown, Spin } from "antd";
import { Check, ChevronsUpDown, LogOut, Menu as MenuIcon } from "lucide-react";
import { Suspense, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { NavItem } from "../components/NavItem";
import { timeAgo } from "../lib/format";
import { useAlerts } from "../lib/queries";
import { signOut, switchOrg, useSession } from "../lib/session";
import { canSee, ROUTES } from "./routes";

const GROUPS = ["Observe", "Configure", "Connect", "Organization"] as const;

function OrgSwitcher() {
  const profile = useSession((s) => s.profile)!;
  const navigate = useNavigate();
  const org = profile.org!;
  const others = profile.orgs.length > 1;
  const trigger = (
    <button type="button" className="flex h-14 w-full cursor-pointer items-center gap-3 border-0 border-b border-solid border-navy-700 bg-transparent px-5 text-left" disabled={!others}>
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center bg-brick font-grotesk text-[13px] font-bold text-white">{org.name.slice(0, 1).toUpperCase()}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-grotesk text-[14px] font-bold tracking-tight text-white">{org.name}</span>
        <span className="block text-[11px] text-navy-200">{org.role}</span>
      </span>
      {others && <ChevronsUpDown size={14} className="text-navy-200" />}
    </button>
  );
  if (!others) return trigger;
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: profile.orgs.map((o) => ({
          key: o.id,
          label: (
            <span className="flex items-center gap-3">
              <span className="flex-1">
                <span className="block font-medium">{o.name}</span>
                <span className="block text-[11px] text-muted">{o.role}</span>
              </span>
              {o.id === org.id && <Check size={14} />}
            </span>
          ),
        })),
        onClick: async ({ key }) => {
          if (key === org.id) return;
          await switchOrg(key);
          navigate("/");
        },
      }}
    >
      {trigger}
    </Dropdown>
  );
}

export default function CorePage() {
  const profile = useSession((s) => s.profile)!;
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visible = ROUTES.filter((r) => r.label && canSee(r, profile.menu));
  const alertsVisible = visible.some((r) => r.path === "/alerts");
  const { data: open } = useAlerts({ state: "open", limit: 200 }, alertsVisible);
  const firing = open?.filter((a) => a.state === "firing").length ?? 0;

  const isActive = (path: string) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));
  const current = visible.find((r) => isActive(r.path));
  const sub = profile.subscription;

  const nav = (after?: () => void) => (
    <div className="flex-1 overflow-y-auto px-3 py-5">
      {GROUPS.map((group) => {
        const items = visible.filter((r) => r.group === group);
        if (!items.length) return null;
        return (
          <div key={group} className="mb-5">
            <p className="mb-2 px-4 font-grotesk text-[10px] font-semibold uppercase tracking-[0.16em] text-navy-500">{group}</p>
            <ul className="m-0 p-0">
              {items.map((r) => {
                const Icon = r.icon!;
                return (
                  <NavItem
                    key={r.path}
                    icon={<Icon size={17} strokeWidth={1.8} />}
                    label={r.label!}
                    active={isActive(r.path)}
                    badge={r.path === "/alerts" ? firing : undefined}
                    onClick={() => {
                      navigate(r.path);
                      after?.();
                    }}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const footer = (
    <div className="border-0 border-t border-solid border-navy-700 p-4">
      <div className="flex items-center gap-3">
        <Avatar size={32} style={{ backgroundColor: "#3A4078", fontFamily: "var(--font-grotesk)" }}>
          {profile.user.name.slice(0, 1).toUpperCase()}
        </Avatar>
        <div className="min-w-0">
          <p className="m-0 truncate text-sm font-medium text-white">{profile.user.name}</p>
          <p className="m-0 truncate text-[11px] text-navy-200">{profile.user.email}</p>
        </div>
      </div>
      <p onClick={() => void signOut()} className="m-0 mt-3 flex cursor-pointer items-center gap-3 rounded-md px-4 py-2 font-grotesk text-sm font-medium text-navy-200 transition-all duration-200 hover:bg-navy-800 hover:text-white">
        <LogOut size={15} /> Sign out
      </p>
    </div>
  );

  return (
    <div className="flex h-dvh flex-col lg:flex-row">
      <nav className="sticky top-0 flex h-14 items-center justify-between border-0 border-b border-solid border-line bg-white px-4 lg:hidden">
        <button type="button" className="btn-sharp btn-ghost" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <MenuIcon size={20} />
        </button>
        <span className="font-grotesk text-[16px] font-bold text-ink">{profile.org?.name}</span>
        <span className="w-10" />
      </nav>

      <aside className="hidden h-screen flex-col border-0 border-r border-solid border-navy-700 bg-navy lg:flex" style={{ minWidth: 240, maxWidth: 240 }}>
        <OrgSwitcher />
        {nav()}
        {footer}
      </aside>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} placement="left" width={280} closable={false} styles={{ body: { padding: 0, background: "#0D1245", display: "flex", flexDirection: "column" } }}>
        <OrgSwitcher />
        {nav(() => setMobileOpen(false))}
        {footer}
      </Drawer>

      <div className="flex min-h-0 max-w-full flex-1 flex-col overflow-hidden bg-canvas lg:max-w-screen-minus-240">
        <header
          className="hidden h-16 flex-shrink-0 items-center justify-between border-0 border-b border-solid border-line px-6 lg:flex"
          style={{ background: "rgba(247,248,252,0.85)", backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)" }}
        >
          <div className="flex items-center gap-2 font-grotesk text-[13px]">
            <span className="text-muted">{profile.org?.name}</span>
            <span className="text-line-soft">/</span>
            <span className="font-semibold text-ink">{current?.label ?? "Pulse"}</span>
          </div>
          <div className="flex items-center gap-4">
            {sub?.status === "trialing" && sub.endDate && (
              <span className="badge badge-info badge-plain">Trial · ends {timeAgo(sub.endDate)}</span>
            )}
            {alertsVisible && (
              <button type="button" onClick={() => navigate("/alerts")} className="btn-sharp btn-secondary btn-sm">
                {firing ? <span className="live-dot" /> : null}
                <span className="tnum">{firing}</span> firing
              </button>
            )}
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Suspense
            fallback={
              <div className="flex h-64 items-center justify-center">
                <Spin />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
